import _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";

import { EmptyState, useDSContext } from "@toddle-edu/ds-web";
import { ChatIllustration } from "@toddle-edu/ds-theme";
import { generateRandomId } from "Utils";
import { useI18n, useMount } from "Hooks";
import withLoader from "UIComponents/GqlWithLoader";
import ChatbarWrapper from "UIComponents/ChatbarWrapper";

import CommentItem, { CommentItemV2 } from "../CommentItem";
import classes from "./CommentFeed.scss";
import classNames from "classnames";

const MESSAGE_DATA = { label: "", attachments: [] };

const CommentFeed = props => {
  const {
    hasNextPage,
    comments,
    userId,
    fetchMoreFeed,
    nodeId,
    collaborators,
    disableTagging,
    canCreateComment,
    parentType,
    canCreateAudioComment,
    mode,
    isRightPaneChat,
    handleClickDelete,
    placeholder,
    editComment,
    createNewComment,
    networkStatus,
    totalCounts,
    markCommentAsRead,
    testId,
    commentFeedContainerClass,
    noCommentContainerClass,
  } = props;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";
  const { t } = useI18n(["common"]);

  const commentFeedRef = useRef(null);
  const commentItemRef = useRef({});

  const updateCommentItemRef = (ref, index) => {
    commentItemRef.current[index] = ref;
  };

  const [editCommentId, setEditCommentId] = useState(null);

  useMount(() => {
    markCommentAsRead();
  });

  useEffect(() => {
    setScrollToBottom();
  }, [nodeId]);

  useEffect(() => {
    if (mode === "view") {
      setEditCommentId(null);
    }
  }, [mode]);

  MESSAGE_DATA["id"] = generateRandomId();

  const handleClickEdit = ({ commentId }) => {
    setEditCommentId(commentId);
  };

  const setScrollToBottom = () => {
    if (commentFeedRef.current) {
      commentFeedRef.current.scrollTop = commentFeedRef.current.scrollHeight;
    }
  };

  const sendNewMessage = params => {
    createNewComment(params);
    setTimeout(setScrollToBottom);
  };

  const handleScroll = async () => {
    if (commentFeedRef.current) {
      if (
        commentFeedRef.current.scrollTop === 0 &&
        hasNextPage &&
        networkStatus !== 3
      ) {
        await fetchMoreFeed();

        /**
         * setTimeout is required to make sure that component is rerendered with new comments
         * before scrolling, otherwise scrolling will not work properly because, commentItemRef.current points to different
         * nodes before rendering and after rendering and we need data after rendering, to get correct scroll position.
         */
        setTimeout(() => {
          const commentsFetchedCount = _.size(comments);
          const scrollIndex =
            totalCounts - commentsFetchedCount <= 10
              ? (totalCounts % 10) - 1
              : 9;
          if (scrollIndex >= 0 && commentFeedRef.current) {
            commentFeedRef.current.scrollTop =
              commentItemRef.current[scrollIndex]?.getBoundingClientRect().top -
              commentFeedRef.current?.getBoundingClientRect().top;
          }
        }, 0);
      }
    }
  };

  const containerClasses = classNames(
    classes.container,
    {
      [classes.containerV2]: isDSV2,
    },
    commentFeedContainerClass
  );

  return (
    <React.Fragment>
      <div
        className={containerClasses}
        ref={commentFeedRef}
        onScroll={handleScroll}
      >
        {_.size(comments) > 0 ? (
          _.map(comments, (comment, index) => {
            const commentItemProps = {
              commentData: comment.node,
              key: comment.node.id,
              isRightPaneChat,
              userId,
              handleClickDelete,
              canCreateAudioComment,
              disableTagging,
              collaborators,
              placeholder,
              parentType,
              mode,
              handleClickEdit,
              editCommentId,
              editComment,
              getCommentItemRef: ref => updateCommentItemRef(ref, index),
              chatBarScrollParent: commentFeedRef.current,
              testId,
            };

            return isDSV2 ? (
              <CommentItemV2 {...commentItemProps} />
            ) : (
              <CommentItem {...commentItemProps} />
            );
          })
        ) : (
          <NoCommentComp
            t={t}
            noCommentContainerClass={noCommentContainerClass}
          />
        )}
      </div>
      {canCreateComment ? (
        <div className={classes.chatBarWrapper}>
          <ChatbarWrapper
            messageData={MESSAGE_DATA}
            sendMessage={sendNewMessage}
            isTaggingEnabled={!disableTagging}
            collaborators={collaborators}
            parentType={parentType}
            inputTextPlaceHolder={placeholder}
            showAddButton={false}
            isAudioRecordingEnabled={canCreateAudioComment}
            shouldExecuteMessageDataUseEffect={false}
            testIdPrefix={testId}
          />
        </div>
      ) : null}
    </React.Fragment>
  );
};

const NoCommentComp = ({ t, noCommentContainerClass }) => {
  return (
    <div
      className={classNames(
        classes.noCommentContainer,
        noCommentContainerClass
      )}
    >
      <EmptyState
        illustration={ChatIllustration}
        subtitle={t("no_comment_in_thread")}
      />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  return {
    isData: ownProps.isData,
    isLoading: ownProps.isLoading,
    containerStyle: { height: "100%" },
  };
};

export default compose(connect(mapStateToProps), withLoader)(CommentFeed);
